/**
 * Triangle Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * Right-triangle cushion = single piece, 3 dimensions: Length, Width, Thickness
 * Shape selection: Triangle (90°) or Corner Tufted Triangle (+$35)
 * Pricing: bench formula with bounding rectangle (L × W × T)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  /* Premium fabric grades (RMB per m2); standard falls back to FABRIC_PRICE_RMB. Driven by fabric `grade:` tag. */
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;   /* Marine fabric target margin 62% (standard 60%); used when grade:marine is selected */
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    shape:       { triangle: 0, tufted_triangle: 35 },
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    triangle: 'Triangle (90°)', tufted_triangle: 'Corner Tufted Triangle',
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: '', L: 0, W: 0, T: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bounding rectangle: L × W × T) ─── */
  function calcBasePrice(L, W, T, ec) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam), bounding rectangle L×W, fabric
     grade auto via fabricRate, at COVER_COST_RATIO (45% margin). Flat $30/$50 gone. */
  function extraCoverUnit() {
    var L = state.L, W = state.W, T = state.T;
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'No extra cover'; continue; }
      var label = (n === 1 ? '1 set' : n + ' sets');
      opts[i].text = unit > 0 ? label + ' (+$' + (unit * n) + ')' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.shape[state.shape] || 0)
      + (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function isComplete() {
    return state.shape && state.L >= 6 && state.W >= 6 && state.T >= 1
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Right-triangle cushion in isometric projection
     Uses same isometric axes as other configurators:
       dirLen   = [0.98776, -0.15596]   (horizontal, width edge)
       dirWidth = [-0.35120, -0.93660]  (diagonal, length/depth edge)

     Triangle top face: right angle at bottom-left (origin)
       A (bottom-left) → B (bottom-right, along width) → C (top-left, along length)
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var uL = state.L, uW = state.W, uT = state.T;
    var empty = (!uL && !uW && !uT);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="tr-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Isometric direction vectors ── */
    var dirLen   = [0.98776, -0.15596];
    var dirWidth = [-0.35120, -0.93660];

    /* ── Compute pixel sizes ── */
    var widthPx, lengthPx, seatH;
    if (empty) {
      widthPx = 220; lengthPx = 180; seatH = 16;
    } else {
      var eW = uW || 24, eL = uL || 24, eT = uT || 3;
      var scale = clamp(220 / Math.max(eW, eL), 2, 6);
      widthPx  = clamp(eW * scale, 80, 320);
      lengthPx = clamp(eL * scale, 80, 320);
      seatH    = clamp(eT * 6, 8, 40);
    }

    /* ── Triangle corners (top face) ── */
    var origin = [120, 270];
    var A = origin;
    var B = [A[0] + dirLen[0] * widthPx, A[1] + dirLen[1] * widthPx];
    var C = [A[0] + dirWidth[0] * lengthPx, A[1] + dirWidth[1] * lengthPx];

    var Ab = [A[0], A[1] + seatH];
    var Bb = [B[0], B[1] + seatH];
    var Cb = [C[0], C[1] + seatH];

    /* ── Auto-fit viewBox ── */
    var allPts = [A, B, C, Ab, Bb, Cb];
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (var i = 0; i < allPts.length; i++) {
      if (allPts[i][0] < minX) minX = allPts[i][0];
      if (allPts[i][0] > maxX) maxX = allPts[i][0];
      if (allPts[i][1] < minY) minY = allPts[i][1];
      if (allPts[i][1] > maxY) maxY = allPts[i][1];
    }
    var mrgL = 70, mrgR = 55, mrgT = 35, mrgB = 45;
    var vw = Math.ceil(maxX - minX + mrgL + mrgR);
    var vh = Math.ceil(maxY - minY + mrgT + mrgB);
    var vx = Math.floor(minX - mrgL);
    var vy = Math.floor(minY - mrgT);
    svg.setAttribute('viewBox', vx + ' ' + vy + ' ' + vw + ' ' + vh);

    /* ── Straps (drawn behind cushion) ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;
      function drawTies(cx_, cy_, dirX) {
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      drawTies(Ab[0], Ab[1], -1);
      drawTies(Bb[0], Bb[1], 1);
      if (state.straps === 'four') {
        var midAC = [(A[0] + C[0]) / 2, (A[1] + C[1]) / 2];
        drawTies(midAC[0], midAC[1] + seatH, -1);
      }
    }

    /* ── Main faces (painter's algorithm: far → near) ── */
    h += '<polygon points="' + [B, C, Cb, Bb].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [A, C, Cb, Ab].map(pt).join(' ')
      + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [A, B, C].map(pt).join(' ')
      + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [A, B, Bb, Ab].map(pt).join(' ')
      + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Right-angle marker on top face ── */
    var raSize = 12;
    var ra1 = [A[0] + dirLen[0] * raSize, A[1] + dirLen[1] * raSize];
    var ra2 = [ra1[0] + dirWidth[0] * raSize, ra1[1] + dirWidth[1] * raSize];
    var ra3 = [A[0] + dirWidth[0] * raSize, A[1] + dirWidth[1] * raSize];
    h += '<polyline points="' + [ra1, ra2, ra3].map(pt).join(' ')
      + '" fill="none" stroke="' + strokeC + '" stroke-width="1" opacity="0.5"/>';

    /* ── Tufted dots (when tufted_triangle shape selected) ── */
    if (state.shape === 'tufted_triangle' && !empty) {
      var tRows = Math.max(2, Math.min(4, Math.round(uL / 10)));
      var tCols = Math.max(2, Math.min(4, Math.round(uW / 8)));
      var dotFill = darken(topC, 0.12);
      for (var row = 0; row < tRows; row++) {
        for (var col = 0; col < tCols; col++) {
          var du = (col + 1) / (tCols + 1);
          var dv = (row + 1) / (tRows + 1);
          if (du + dv > 0.92) continue;
          var dp = [
            A[0] + du * (B[0] - A[0]) + dv * (C[0] - A[0]),
            A[1] + du * (B[1] - A[1]) + dv * (C[1] - A[1])
          ];
          h += '<circle cx="' + f(dp[0]) + '" cy="' + f(dp[1]) + '" r="2" fill="' + dotFill + '" stroke="' + strokeC + '" stroke-width="0.5" opacity="0.5"/>';
          h += '<circle cx="' + f(dp[0] - 0.4) + '" cy="' + f(dp[1] - 0.4) + '" r="0.7" fill="#fff" opacity="0.35"/>';
        }
      }
    }

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8';
      var pw = 2.2;
      h += '<polygon points="' + [A, B, C].map(pt).join(' ')
        + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        h += '<polyline points="' + [Cb, Ab, Bb].map(pt).join(' ')
          + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip on bottom ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var padOffset = 1, padThick = 4, padInset = 5;
      var normalDir = [-dirLen[1], dirLen[0]];
      var edgeLenPx = Math.sqrt(Math.pow(Bb[0] - Ab[0], 2) + Math.pow(Bb[1] - Ab[1], 2));

      function stripQuad(p1, p2, t0, t1) {
        var elen = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
        var q1 = [p1[0] + (p2[0] - p1[0]) * (t0 / elen), p1[1] + (p2[1] - p1[1]) * (t0 / elen)];
        var q2 = [p1[0] + (p2[0] - p1[0]) * (t1 / elen), p1[1] + (p2[1] - p1[1]) * (t1 / elen)];
        var q1o = [q1[0] + normalDir[0] * padOffset, q1[1] + normalDir[1] * padOffset];
        var q2o = [q2[0] + normalDir[0] * padOffset, q2[1] + normalDir[1] * padOffset];
        var q3 = [q2[0] + normalDir[0] * (padOffset + padThick), q2[1] + normalDir[1] * (padOffset + padThick)];
        var q4 = [q1[0] + normalDir[0] * (padOffset + padThick), q1[1] + normalDir[1] * (padOffset + padThick)];
        return [q1o, q2o, q3, q4];
      }

      if (state.antislip === 'velcro') {
        var strips = 4, gapPx = edgeLenPx / strips;
        for (var vi = 0; vi < strips; vi++) {
          var t0 = vi * gapPx + 3, t1 = (vi + 1) * gapPx - 3;
          if (t1 <= t0) continue;
          var quad = stripQuad(Ab, Bb, t0, t1);
          h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var quad = stripQuad(Ab, Bb, padInset, edgeLenPx - padInset);
        h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Dimension labels ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = 10;
    var hug = 20;

    var lblL = uL ? ' — ' + uL + '″' : '';
    var lblW = uW ? ' — ' + uW + '″' : '';
    var lblT = uT ? ' — ' + uT + '″' : '';

    // Width — below front edge (A → B)
    var wla1 = [Ab[0], Ab[1] + hug];
    var wla2 = [Bb[0], Bb[1] + hug];
    h += '<line x1="' + f(Ab[0]) + '" y1="' + f(Ab[1]) + '" x2="' + f(wla1[0]) + '" y2="' + f(wla1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(Bb[0]) + '" y1="' + f(Bb[1]) + '" x2="' + f(wla2[0]) + '" y2="' + f(wla2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(wla1[0]) + '" y1="' + f(wla1[1]) + '" x2="' + f(wla2[0]) + '" y2="' + f(wla2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#tr-arr)" marker-end="url(#tr-arr)"/>';
    h += '<text x="' + f((wla1[0] + wla2[0]) / 2) + '" y="' + f((wla1[1] + wla2[1]) / 2 + 14)
      + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '">Width' + lblW + '</text>';

    // Length — left side (A → C)
    var lla1 = [A[0] - hug, A[1]];
    var lla2 = [C[0] - hug, C[1]];
    h += '<line x1="' + f(A[0]) + '" y1="' + f(A[1]) + '" x2="' + f(lla1[0]) + '" y2="' + f(lla1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(C[0]) + '" y1="' + f(C[1]) + '" x2="' + f(lla2[0]) + '" y2="' + f(lla2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(lla1[0]) + '" y1="' + f(lla1[1]) + '" x2="' + f(lla2[0]) + '" y2="' + f(lla2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#tr-arr)" marker-end="url(#tr-arr)"/>';
    var lMid = [(lla1[0] + lla2[0]) / 2, (lla1[1] + lla2[1]) / 2];
    h += '<text x="' + f(lMid[0] - 6) + '" y="' + f(lMid[1])
      + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC
      + '" transform="rotate(-70 ' + f(lMid[0] - 6) + ' ' + f(lMid[1]) + ')">Length' + lblL + '</text>';

    // Thickness — right side vertical at B
    var thx = Bb[0] + hug;
    h += '<line x1="' + f(B[0]) + '" y1="' + f(B[1]) + '" x2="' + f(thx) + '" y2="' + f(B[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(Bb[0]) + '" y1="' + f(Bb[1]) + '" x2="' + f(thx) + '" y2="' + f(Bb[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(B[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(Bb[1] - 2)
      + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#tr-arr)" marker-end="url(#tr-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((B[1] + Bb[1]) / 2 + 4)
      + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC
      + '">T' + lblT + '</text>';

    if (empty) {
      var hintX = (Ab[0] + Bb[0]) / 2;
      var hintY = Math.max(wla1[1], wla2[1]) + 26;
      h += '<text x="' + f(hintX) + '" y="' + f(hintY) + '" text-anchor="middle" font-size="10.5" font-family="Poppins,sans-serif" fill="#8A8A85">Enter dimensions below to see your cushion</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.L && state.W && state.T) parts.push(state.L + ' × ' + state.W + ' × ' + state.T + '″');
    if (state.shape) parts.push(LABELS[state.shape] || state.shape);
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your triangle cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.L = parseFloat(els.inputL.value) || 0;
    state.W = parseFloat(els.inputW.value) || 0;
    state.T = parseFloat(els.inputT.value) || 0;
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    ['inputL', 'inputW', 'inputT'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var hasDim = state.L >= 6 && state.W >= 6 && state.T >= 1;
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var base = hasDim ? calcBasePrice(state.L, state.W, state.T) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(base)];
      if (ADDON.shape[state.shape])             parts.push('Shape +$' + ADDON.shape[state.shape]);
      if (ADDON.piping[state.piping])           parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])           parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])           parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip])       parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ec > 0)                               parts.push('Covers +$' + (extraCoverUnit() * ec));
      if (ADDON.throwPillow[state.throwPillow]) parts.push('Throw Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Pillow Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        var fp = Math.round(base + addonTotal);
        btn.textContent = 'Add to order — $' + fp + ' →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!state.shape) missing.push('shape');
        if (!hasDim) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: base !== null ? Math.round(base + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var base = calcBasePrice(state.L, state.W, state.T);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Shape': LABELS[state.shape] || state.shape,
      'Length': state.L + '"', 'Width': state.W + '"', 'Thickness': state.T + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Fabric Grade'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s)';
    if (state.throwPillow !== 'none') props['Throw Pillow'] = state.throwPillow.replace('pcs_', ' pcs ');
    if (state.pillowCovers !== 'none') props['Pillow Covers'] = state.pillowCovers.replace('pcs_', ' pcs ');
    if (state.armrestPillow !== 'none') props['Armrest Pillow'] = state.armrestPillow.replace('_', ' ');
    if (state.zipperPlacement) {
      props['Zipper Placement'] = state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    var apiPayload = {
      length: state.L, width: state.W, thickness: state.T,
      shape: state.shape, piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow,
      pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };

    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── DOM transformation ─── */
  function transformDOM() {
    var svg = document.getElementById('cc-dim-svg');
    if (svg) svg.style.maxHeight = '240px';

    // Replace shape cards: 2 triangle options instead of default 3 bench shapes
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.innerHTML = ''
        + '<div class="cc-opt-card" data-group="shape" data-value="triangle">'
        + '  <div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/Triangle_90-degree_custom_cushion_4bb421cc-84cc-431c-83ec-ec940db211c6.jpg?v=1777218010" alt="Triangle (90°)"></div>'
        + '  <div class="cc-opt-name">Triangle (90°)</div><div class="cc-opt-price">included</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="shape" data-value="tufted_triangle">'
        + '  <div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/corner_tufted_cushion.jpg?v=1783665920" alt="Corner Tufted Triangle"></div>'
        + '  <div class="cc-opt-name">Corner Tufted</div><div class="cc-opt-price">+$35</div>'
        + '</div>';
    }

    // cc-dim-l → Length
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Length (in) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'e.g. 24'; inputL.max = '100'; }

    // cc-dim-w → Width
    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Width (in) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) inputW.placeholder = 'e.g. 24';

    // cc-dim-t → Thickness
    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Thickness (in) <span class="cc-req">*</span>';

    // Hide back dims row (not needed — only 3 inputs)
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      backDims[i].style.display = 'none';
    }

    // Hide price area above SVG
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatDims) dimsContainer.appendChild(seatDims);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }

    // Renumber steps
    var secHeads = document.querySelectorAll('.cc-sec-head .cc-sn');
    var stepNum = 1;
    for (var k = 0; k < secHeads.length; k++) {
      var sec = secHeads[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none') {
        secHeads[k].textContent = stepNum++;
      }
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.inputL       = document.getElementById('cc-dim-l');
    els.inputW       = document.getElementById('cc-dim-w');
    els.inputT       = document.getElementById('cc-dim-t');
    els.extraCovers    = document.getElementById('cc-extra-covers');
    els.throwPillow    = document.getElementById('cc-throw-pillow');
    els.pillowCovers   = document.getElementById('cc-pillow-covers');
    els.armrestPillow  = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    els.inputL.addEventListener('input', recalc);
    els.inputW.addEventListener('input', recalc);
    els.inputT.addEventListener('input', recalc);
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.TriangleCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
